import { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { sha256 } from "js-sha256";
import { BO_CORE_DEFAULT_VALUE, getAxiosInstances, getLocaleNameByTag, isCancelledError, toastError } from "../AppUtils";
import store from "../../store";
import { fetchAuthUser, setApplicationLocale, setAuthenticatedUser } from "../../reducers/userReducer";
import { Routes, PublicRoutes, UserRoles } from "../appEnums/AppEnums";
import { LOCAL_STORAGE_ITEM_LOCALE } from "../../config/app.config";
import AxiosActions from "../../config/axios/AxiosActions";
import { durationToExactDate, getLocales } from "../DateTimeUtils";
import { setSimpleSpinner } from "../../reducers/spinnerReducer";
import { withPreservedEntryParams } from "../publicPageParams";
import {
    TokenStore,
    buildTokenEntry,
    clearAllStores,
    getActiveStore,
    getCsTenantPointer,
    getCurrentTenantId,
    getTenantIdFromAccessToken,
    hasMainStoreToken,
    isAssumeMode,
    isExpired,
    removeActiveAssumedToken,
    seedAssumedToken,
    setAssumeMode,
    setCsTenantPointer,
    setCurrentTenantId,
} from "./TokenStore";

/**
 * NG-8051: the token layer is backed by the localStorage TokenStore, which holds one slot per
 * tenant plus a separate assumed-identity store.
 *
 * PER-ORIGIN: the tokens this replaced were cookies shared across subdomains via
 * `domain: rootHostName`. localStorage is per-origin, so a session does NOT carry across tenant
 * subdomains, `go.*` short URLs, or the OAuth `/authorize` round-trip — each origin authenticates
 * on its own. Keep that in mind before introducing any cross-subdomain hand-off.
 */

export interface UserPreference {
    locale: { value: string; label: string };
}

// export const AVAILABLE_USER_ROLES_IN_ACTIVE_MANAGEMENT_MODE = [UserRoles.Employee, UserRoles.Manager, UserRoles.SA];

let authUser: any = null;

const SESSION_STORAGE_CODE_VERIFIER = "code_verifier";

export function getAuthUser() {
    return authUser;
}

export function setAuthUser(value) {
    authUser = value;
}

export const getAccessToken = () => {
    // Return the token even when expired — the 401/refresh machinery handles staleness.
    return getActiveStore().get(getCurrentTenantId())?.accessToken;
};
export const isRefreshTokenPresent = (): boolean => {
    const entry = getActiveStore().get(getCurrentTenantId());
    return !!entry?.refreshToken && !isExpired(entry.refreshTokenExpiry);
};
export const isAccessTokenPresent = (): boolean => {
    // localStorage entries persist past expiry, so presence must be expiry-aware — otherwise
    // route guards and the 401 logout branch never fire.
    const entry = getActiveStore().get(getCurrentTenantId());
    return !!entry?.accessToken && !isExpired(entry.accessTokenExpiry);
};
export const getRefreshToken = () => {
    return getActiveStore().get(getCurrentTenantId())?.refreshToken;
};
export const isAssumingIdentity = () => isAssumeMode();
export const mySelfAuthHelper = (showSpinner?: boolean, signal?: AbortSignal): Promise<AxiosResponse> => {
    return AxiosActions.get(getAxiosInstances().V2, `user/myself`, { signal }, showSpinner === true);
};
export const mutateMySelfResponse = (response: AxiosResponse) => {
    const mutatedResponse = response;

    if (mutatedResponse.data.customerSupport) {
        mutatedResponse.data.roles.push(UserRoles.CS);
    }

    return mutatedResponse;
};
export const updateUserLocaleAuthHelper = (localeTag: string): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `user/locale?locale=${localeTag}`, {}, {}, false);
};
/** Drop the active store's slot for the current tenant. */
export const clearActiveTokens = () => {
    getActiveStore().remove(getCurrentTenantId());
};
/**
 * Point the TokenStore slot at the tenant being switched to. Must be called after the refresh
 * that mints the new-context tokens and immediately before they are stored.
 */
export const setActiveTenantContext = (tenantId: number) => {
    setCurrentTenantId(tenantId);
};
export const processMySelfRequestError = (e) => {
    if (isCancelledError(e)) {
        return;
    }

    store.dispatch(setSimpleSpinner(false));
    store.dispatch(fetchAuthUser(false));
    clearAllStores();

    window.location.href = PublicRoutes.INIT_AUTH;
};
export const storeTokens = (accessToken: string, refreshToken: string, accessTokenExpiry: number, refreshTokenExpiry: number) => {
    // Key the slot by the token's own tenantId claim, not the tab context: the BE can mint a
    // token for a different tenant than the tab is on (see getTenantIdFromAccessToken), and the
    // tab context can also move while a token response is in flight. Trusting the claim keeps a
    // wrong-tenant token from overwriting another tenant's slot; the tab context is only a
    // fallback for tokens whose claim is unreadable.
    const claimTenantId = getTenantIdFromAccessToken(accessToken);
    getActiveStore().set(claimTenantId ?? getCurrentTenantId(), buildTokenEntry(accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry));
};
/**
 * The slot a token refresh must write back to, captured at refresh START. The write cannot key off
 * the wall-clock tenant context: a tenant switch (or assume-mode flip) can land while the refresh
 * round-trip is in flight, and the response belongs to the slot whose refresh token was spent —
 * not wherever the tab points by the time it arrives.
 */
export interface RefreshSlot {
    tenantId: number;
    store: TokenStore;
}

export const captureRefreshSlot = (): RefreshSlot => ({ tenantId: getCurrentTenantId(), store: getActiveStore() });

/**
 * Store a refresh response into the slot captured at refresh start. A token whose tenantId claim
 * names a DIFFERENT tenant is rejected: the BE's refresh grant stamps the user's persisted
 * current-tenant context, which goes stale when this app switches tenants from cached per-tenant
 * tokens without a switch-tenant call — accepting the token would poison the slot with another
 * tenant's credentials. The dead slot is dropped (its refresh token was just spent) and false is
 * returned so the caller treats it as a failed refresh.
 */
export const storeRefreshedTokens = (
    accessToken: string,
    refreshToken: string,
    accessTokenExpiry: number,
    refreshTokenExpiry: number,
    slot: RefreshSlot,
): boolean => {
    const claimTenantId = getTenantIdFromAccessToken(accessToken);
    if (claimTenantId !== null && claimTenantId !== slot.tenantId) {
        // eslint-disable-next-line no-console
        console.error(
            `Auth: refresh for tenant ${slot.tenantId} returned a token minted for tenant ${claimTenantId} — discarding it (stale backend tenant context).`,
        );
        slot.store.remove(slot.tenantId);
        return false;
    }
    slot.store.set(slot.tenantId, buildTokenEntry(accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry));
    return true;
};

export const processMySelfRequest = (response: AxiosResponse) => {
    // Pure response -> redux. The token slot and tenant context are established up front by the
    // auth entry points (establishSession / assumeIdentityHere) which seed under the real tenant,
    // so there is no slot reconciliation to do here.
    const mutatedResponse = mutateMySelfResponse(response);
    setAuthUser(mutatedResponse.data);
    store.dispatch(setAuthenticatedUser(getAuthUser()));

    return mutatedResponse;
};
export const logoutHelper = () => {
    return AxiosActions.postErrorsAreThrown(getAxiosInstances().AUTH, "user/logout", {}, { withCredentials: true }, false);
};

/**
 * Wipe THIS identity's assumed-token slot on release/restore (NG-8061). Removes only the active
 * tenant's slot, so a different assumed identity (other tenant) in another tab is untouched. The
 * removal fires a `storage` event that reverts same-tenant sibling tabs to the CS user (App.tsx
 * listener). Guarded so it never runs outside assume mode.
 */
export const wipeActiveAssumedEntry = (): void => {
    if (isAssumeMode()) {
        removeActiveAssumedToken();
    }
};

/**
 * Return THIS tab from an assumed identity to the CS user (NG-8061 multi-tab). The single revert
 * exit shared by every trigger: the explicit Release button, a forced `endSession`, a sibling tab
 * releasing (storage event), and a reload landing on an emptied assumed slot (bootstrap guard).
 *
 * Re-pins to the CS tenant captured at assume-start (`th_cs_tenant`) and lands on System Support —
 * the CS's home, where access is guaranteed because the Assume action is only reachable from there.
 * If the CS's own tokens are gone too (a full logout wiped both stores), there is nothing to restore,
 * so go to login. `replace` so the emptied assume URL is not left in history.
 */
export const revertToCsContext = (): void => {
    const csTenantId = getCsTenantPointer();
    setAssumeMode(false);
    if (csTenantId !== null && hasMainStoreToken(csTenantId)) {
        setCurrentTenantId(csTenantId);
        window.location.replace(Routes.SYSTEM_SUPPORT);
        return;
    }
    window.location.replace(PublicRoutes.LOGIN);
};

/**
 * True when THIS tab claims an assumed identity (`assumeMode`) but its assumed-token slot is gone —
 * a sibling tab released it, or this tab reloaded after that happened (NG-8061 multi-tab).
 */
const isAssumedSessionOrphaned = (): boolean => isAssumeMode() && getActiveStore().get(getCurrentTenantId()) === undefined;

/**
 * Bootstrap guard + cross-tab reaction for the assume flow (NG-8061). Called at app start and from
 * App.tsx's `storage` listener: if this tab's assumed identity has been released out from under it,
 * revert to the CS user before any authed call seeds CS tokens into the emptied assumed slot. No-op
 * for a healthy assume tab (slot present) or any non-assume tab.
 */
export const reconcileOrphanedAssumeSession = (): void => {
    if (isAssumedSessionOrphaned()) {
        revertToCsContext();
    }
};

export const restoreIdentity = async () => {
    // NG-8061 multi-tab: drop only THIS identity's slot (per-tenant, so sibling assume tabs on
    // other tenants survive), then revert this tab to the CS user via the shared pointer path.
    // `wipeActiveAssumedEntry` fires the storage event that reverts same-tenant siblings too.
    wipeActiveAssumedEntry();
    revertToCsContext();
};

/**
 * Release the assumed identity from the header button (NG-8061). Delegates to `restoreIdentity`,
 * which wipes this identity's slot and reverts the tab to the CS user (landing on System Support).
 * Reverting — rather than `window.close()` — keeps a single, consistent exit for every tab of the
 * identity (including manually-opened tabs a script cannot close) and matches how sibling tabs
 * react to the wipe.
 */
export const releaseAssumedIdentity = (): void | Promise<void> => {
    return restoreIdentity();
};

/**
 * Resolve the tenant a freshly-minted token belongs to WITHOUT seeding the store first. The token
 * response carries no tenant, and we won't seed under a sentinel like DEFAULT_TENANT_ID — that is a
 * real tenant and would corrupt slot 1. Instead we call /myself with the token passed explicitly:
 * `withCredentials: false` makes the request interceptor skip its store-based Authorization
 * overwrite (see appendAuthorizationBearerToken), so the call authorizes against this token with no
 * TokenStore state. Throws if the response carries no usable tenant — callers then abort rather
 * than guess. Shared by every fresh-auth entry: login, registration, and assume.
 */
const resolveTenantIdForToken = async (accessToken: string, showSpinner = true): Promise<number> => {
    const response = await AxiosActions.get(
        getAxiosInstances().V2,
        "user/myself",
        { withCredentials: false, headers: { Authorization: `Bearer ${accessToken}` } },
        showSpinner,
    );
    const tenantId = response?.data?.tenantContext?.id;
    if (!Number.isInteger(tenantId) || tenantId <= 0) {
        throw new Error("Auth: /myself returned no usable tenant context");
    }
    return tenantId;
};

const seedAssumedTokens = (accessToken: string, refreshToken: string, expiresIn: number, tenantId: number): void => {
    // expiry comes through as `0` so buildTokenEntry applies its refresh = access * 2 fallback.
    seedAssumedToken(tenantId, buildTokenEntry(accessToken, refreshToken, expiresIn, 0));
};

/**
 * Establish a freshly-authenticated MAIN-store session (login, registration). Resolves the token's
 * real tenant up front and seeds that slot directly — no DEFAULT placeholder, no
 * processMySelfRequest reconciliation. The caller still runs its own /myself afterwards to
 * populate redux.
 *
 * `showSpinner` exists because the tenant resolution above is a real `AxiosActions` call and so raises
 * the global overlay. That is right at a login or a plain registration, where nothing else is on
 * screen, and wrong when the caller is already rendering its own progress UI — NG-8648's developer
 * registration establishes the session mid-checklist, and the overlay covered it (AxiosActions also
 * holds the overlay for a second after the call settles, so it bled into the following step). Default
 * true keeps every existing caller as it was.
 */
export const establishSession = async (
    accessToken: string,
    refreshToken: string,
    accessTokenExpiry: number,
    refreshTokenExpiry: number,
    showSpinner = true,
): Promise<void> => {
    const tenantId = await resolveTenantIdForToken(accessToken, showSpinner);
    setCurrentTenantId(tenantId);
    // getActiveStore() is the main store here (not assume mode), so this seeds tokenStore[tenantId].
    storeTokens(accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry);
};

/**
 * Prepares an assume-identity session for a NEW tab without touching this tab's state: resolves the
 * assumed user's real tenant and seeds the assumed store under it. Returns that tenant id so the
 * caller can open the assume tab on a fresh user gesture
 * (`openAppInNewTab(url, tenantId, { assume: true })`) — deferring the open to a real gesture
 * avoids the popup blocker.
 *
 * Runs in the CS tab (not assume mode), so `getCurrentTenantId()` is the CS's own tenant: record it
 * as the CS-context pointer (NG-8061 multi-tab) so any assume tab can later revert to the CS user.
 */
export const prepareAssumedIdentityForNewTab = async (accessToken: string, refreshToken: string, expiresIn: number): Promise<number> => {
    const assumedTenantId = await resolveTenantIdForToken(accessToken);
    setCsTenantPointer(getCurrentTenantId());
    seedAssumedTokens(accessToken, refreshToken, expiresIn, assumedTenantId);
    return assumedTenantId;
};

export const setCodeVerifierCookies = (codeVerifier: string, codeVerifierExpiry: number) => {
    Cookies.set(SESSION_STORAGE_CODE_VERIFIER, codeVerifier, { expires: durationToExactDate(codeVerifierExpiry, "second") });
};
export const retrievePreferredLocale = (myselfLocale: any) => {
    if (myselfLocale && myselfLocale !== BO_CORE_DEFAULT_VALUE) {
        return myselfLocale;
    }
    const locale: { label: string; value: string } = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_ITEM_LOCALE) as string);
    return locale ? locale.value : getLocales();
};
export const getCodeVerifierCookies = () => {
    return Cookies.get(SESSION_STORAGE_CODE_VERIFIER);
};
export const removeCodeVerifierCookies = () => {
    Cookies.remove(SESSION_STORAGE_CODE_VERIFIER);
};
export const objectToEncodedRequestBody = (object: any): string => {
    const formBody: string[] = [];

    Object.keys(object).forEach((property: string) => {
        const encodedKey = encodeURIComponent(property);
        const encodedValue = encodeURIComponent(object[property]);
        formBody.push(`${encodedKey}=${encodedValue}`);
    });

    return formBody.join("&");
};
export const updateUserLocale = (myselfLocale: any, currentUserPreference: UserPreference) => {
    const preferredLocale = retrievePreferredLocale(myselfLocale);
    if (preferredLocale && preferredLocale !== currentUserPreference.locale.value) {
        store.dispatch(setApplicationLocale({ value: preferredLocale, label: getLocaleNameByTag(preferredLocale) }));
    }
    const saveUserPreferredLocale = preferredLocale !== myselfLocale;
    if (saveUserPreferredLocale) {
        updateUserLocaleAuthHelper(preferredLocale).catch(() => {
            toastError("Failed saving user preferred location.");
        });
    }
};
export const updateUserPreferences = (myselfResponse: any, currentUserPreference: UserPreference) => {
    updateUserLocale(myselfResponse.locale, currentUserPreference);
};
export const isUserProctor = (userData: any): boolean => {
    return userData.roles.includes(UserRoles.Proctor || UserRoles.SA) || getAuthUser().roles.includes(UserRoles.SA);
};
export const isUserOnlyPatient = (userData: any): boolean => {
    if (isUserProctor(userData)) {
        return false;
    }
    return getAuthUser().roles.includes(UserRoles.Patient);
};
/** `showSpinner` defaults to today's behaviour; see establishSession for why a caller would opt out. */
export const mySelfRequest = (userPreference: UserPreference, signal?: AbortSignal, showSpinner = true) => {
    return mySelfAuthHelper(showSpinner, signal)
        .then((response) => {
            updateUserPreferences(response.data, userPreference);
            return response;
        })
        .then(processMySelfRequest)
        .catch(processMySelfRequestError);
};
export const serverLogOut = async () => {
    try {
        // The server call is best-effort (NG-8591). A 401 means the session is already gone — a
        // global logout from another device, or simply an expired token — which is the outcome we
        // want anyway, and any other failure still leaves us holding credentials we cannot use.
        // `logoutHelper` rethrows, so awaiting it bare made the cleanup below conditional on the
        // server answering 200: on a 401 nothing was cleared, the redirect never fired, and the user
        // was stranded on an endless spinner that only a manual refresh recovered. `try/catch` in an
        // async function also covers a synchronous throw out of the helper.
        await logoutHelper();
    } catch {
        // Intentionally ignored — see above.
    }

    // Logout clears every tenant slot, both stores, and the session routing keys.
    clearAllStores();

    // Carry the deep link and branding across (NG-8591): a session revoked from another device is
    // discovered on /login?openApp=…&brandingId=…&mode=dark, and dropping the query string here
    // re-authenticated the visitor into the dashboard in default branding instead of the app they
    // came to open. A sign-out from inside the app carries none of these, so it is unchanged.
    window.location.href = withPreservedEntryParams(PublicRoutes.LOGIN, window.location.search);
};

/**
 * Unified session exit (NG-8061). A regular user is logged out; an assumed session is released back
 * to the CS user instead — per product, an assumed dead-end (e.g. cannot accept the assumed user's
 * terms on their behalf) must not punt the CS to login. Use this at forced-exit points where the
 * legacy behavior was an unconditional logout.
 */
export const endSession = (): Promise<void> => (isAssumingIdentity() ? restoreIdentity() : serverLogOut());

const base64URLEncode = (arrayBuffer) => {
    const binaryString = String.fromCharCode(...new Uint8Array(arrayBuffer));
    return btoa(binaryString).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const generateCodeVerifier = () => {
    const array = new Uint8Array(32); // 32 bytes = 256 bits
    crypto.getRandomValues(array); // Fill with cryptographically secure random values
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, ""); // Base64 URL encode
};

// Generate a code_challenge from the code_verifier
export const generateCodeChallenge = (codeVerifier) => {
    // Hash the UTF-8 encoded code_verifier
    const hashBuffer = sha256.arrayBuffer(codeVerifier);
    // Base64 URL-encode the hash
    return base64URLEncode(hashBuffer);
};
